import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { draftRow, fromRow, type Reminder, type ReminderDraft } from "../lib/reminders";

/* THE COACH'S REMINDERS — THE ONE READ AND THE ONE WRITE PATH (W82 step 6; `coach_reminders`, live 2026-09-26).
 *
 * ⚠️ EVERY `.from("coach_reminders")` IN THE WEB APP IS IN THIS FILE (a source ruler counts them): the Dashboard card,
 * the Reminders page and a client's `Remind me…` window all read through `useReminders` and write through its
 * `write` — add, edit, done/undone, delete — so the row shape (`draftRow`) and the refresh are said once.
 *
 * ⚠️ "MINE" IS EXPLICIT (`.eq("coach_id", coachId)`), not left to the policy alone — the codex's rule for a table whose
 * read policy could one day widen. Today the policy is owner-only, so it changes nothing; it is the line that keeps
 * this screen right on the day it does.
 *
 * ⚠️ AFTER EVERY WRITE the hook re-reads and says so on `window` (`ht:reminders`), so a card and a page mounted at once
 * (the Dashboard behind the window) show one answer. A failed write returns its words and changes nothing on screen.
 */
export const REMINDERS_CHANGED = "ht:reminders";

export type ReminderClient = { id: string; name: string };
export type RemindersState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; rows: Reminder[]; clients: ReminderClient[] };

export type ReminderWrite = {
  add: (d: ReminderDraft) => Promise<string | null>;
  edit: (id: string, d: ReminderDraft) => Promise<string | null>;
  setDone: (id: string, done: boolean) => Promise<string | null>;
  remove: (id: string) => Promise<string | null>;
};

export function useReminders(coachId: string, zone: string): { state: RemindersState; write: ReminderWrite } {
  const [state, setState] = useState<RemindersState>({ status: "loading" });
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    const bump = () => setSeq((n) => n + 1);
    window.addEventListener(REMINDERS_CHANGED, bump);
    return () => window.removeEventListener(REMINDERS_CHANGED, bump);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rows = await supabase.from("coach_reminders")
        .select("id, client_id, title, due_at, done_at, created_at")
        .eq("coach_id", coachId)
        .order("due_at", { ascending: true });
      if (rows.error !== null) {
        if (!cancelled) setState({ status: "error", message: rows.error.message });
        return;
      }
      /* The client picker's names — the ACCEPTED, ACTIVE links (every coach/client policy's own predicate), named from
         their profiles; a client whose profile could not be read is still a client. */
      const links = await supabase.from("coach_clients")
        .select("client_id").eq("coach_id", coachId).eq("status", "active").not("accepted_at", "is", null);
      const ids = (links.data ?? []).map((l) => l.client_id).filter((i): i is string => i !== null);
      const people = ids.length === 0 ? { data: [] as { id: string; first_name: string | null; last_name: string | null }[] }
        : await supabase.from("profiles").select("id, first_name, last_name").in("id", ids);
      const clients = ids.map((id) => {
        const p = (people.data ?? []).find((x) => x.id === id);
        const full = [p?.first_name, p?.last_name].filter((s) => s !== null && s !== undefined && s !== "").join(" ");
        return { id, name: full === "" ? "This client" : full };
      }).sort((a, b) => a.name.localeCompare(b.name));
      if (!cancelled) setState({ status: "ready", rows: rows.data.map(fromRow), clients });
    })();
    return () => { cancelled = true; };
  }, [coachId, seq]);

  const done = useCallback((error: { message: string } | null, what: string): string | null => {
    if (error !== null) return `That reminder ${what}: ${error.message}`;
    window.dispatchEvent(new Event(REMINDERS_CHANGED));
    return null;
  }, []);

  const write: ReminderWrite = {
    add: async (d) => {
      const row = draftRow(d, zone);
      if (row === null) return "That reminder is not complete.";
      const { error } = await supabase.from("coach_reminders").insert(row);
      return done(error, "did not save");
    },
    edit: async (id, d) => {
      const row = draftRow(d, zone);
      if (row === null) return "That reminder is not complete.";
      const { error } = await supabase.from("coach_reminders").update(row).eq("id", id).eq("coach_id", coachId);
      return done(error, "did not save");
    },
    setDone: async (id, isDone) => {
      const { error } = await supabase.from("coach_reminders")
        .update({ done_at: isDone ? new Date().toISOString() : null }).eq("id", id).eq("coach_id", coachId);
      return done(error, isDone ? "was not marked done" : "was not reopened");
    },
    remove: async (id) => {
      const { error } = await supabase.from("coach_reminders").delete().eq("id", id).eq("coach_id", coachId);
      return done(error, "was not deleted");
    },
  };

  return { state, write };
}
